import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, 
  Heart, 
  Shield, 
  Star, 
  Play, 
  PawPrint, 
  Sparkles,
  CheckCircle2,
  ArrowUpRight,
  BadgeCheck
} from 'lucide-react'
import { Button } from "@/components/ui/button"
import { useState } from 'react'

const FinalCTA = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const highlights = [
    {
      icon: Shield,
      text: "Análisis de salud con 90% de precisión",
      subtext: "Resultados validados por veterinarios",
      color: "from-emerald-400 to-emerald-600",
      hoverColor: "from-emerald-300 to-emerald-500"
    },
    {
      icon: Heart,
      text: "Consultas veterinarias 24/7",
      subtext: "Soporte profesional inmediato",
      color: "from-rose-400 to-rose-600",
      hoverColor: "from-rose-300 to-rose-500"
    },
    {
      icon: Star,
      text: "Reportes detallados y personalizados",
      subtext: "Adaptados a cada mascota",
      color: "from-amber-400 to-amber-600",
      hoverColor: "from-amber-300 to-amber-500"
    }
  ];

  return (
    <section className="relative overflow-hidden py-16 sm:py-20 lg:py-24">
      {/* Enhanced Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-orange-600 opacity-95" />
        <div className="absolute inset-0 opacity-5" />
        
        {/* Animated Background Elements */}
        <motion.div 
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 3, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] 
                   from-white via-transparent to-transparent"
        />
        
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <PawPrint className="absolute top-12 -right-6 h-32 w-32 text-white/5 rotate-12" />
          <PawPrint className="absolute bottom-12 -left-6 h-24 w-24 text-white/5 -rotate-12" />
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative max-w-6xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative bg-gradient-to-b from-white/15 to-white/5 
                   backdrop-blur-md rounded-3xl p-6 sm:p-8 lg:p-12 border border-white/20 
                   shadow-[0_8px_32px_rgba(0,0,0,0.12)] overflow-hidden"
        >
          {/* Shine Effect */}
          <motion.div 
            animate={{ 
              x: ["0%", "100%"],
              opacity: [0, 0.5, 0]
            }}
            transition={{ 
              duration: 5,
              repeat: Infinity,
              repeatType: "loop",
              ease: "easeInOut"
            }}
            className="absolute -top-32 left-0 w-96 h-96 
                     bg-gradient-to-r from-transparent via-white/20 to-transparent 
                     transform rotate-45 pointer-events-none"
          />

          <div className="relative z-10">
            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-10 sm:mb-12"
            >
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 
                           rounded-full border border-white/30">
                <BadgeCheck className="h-5 w-5 text-yellow-300" />
                <span className="text-white/90 text-sm sm:text-base font-medium">
                  Certificado por Veterinarios
                </span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 
                           rounded-full border border-white/30">
                <motion.div
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <PawPrint className="h-5 w-5 text-yellow-300" />
                </motion.div>
                <span className="text-white/90 text-sm sm:text-base font-medium">
                  +10,000 Mascotas Felices
                </span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 
                           rounded-full border border-white/30">
                <Star className="h-5 w-5 text-yellow-300 fill-yellow-300" />
                <span className="text-white/90 text-sm sm:text-base font-medium">
                  4.9/5 Calificación
                </span>
              </div>
            </motion.div>

            {/* Main Content */}
            <div className="text-center max-w-4xl mx-auto">
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white 
                         mb-6 sm:mb-8 leading-tight"
              >
                Dale a tu mejor amigo el{' '}
                <span className="relative inline-block">
                  cuidado
                  <motion.div 
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                    className="absolute -bottom-1 left-0 right-0 h-1 sm:h-1.5 
                             bg-gradient-to-r from-yellow-300/60 via-yellow-300 to-yellow-300/60 
                             rounded-full transform origin-left"
                  />
                </span>
                {' '}que merece
              </motion.h2>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-lg sm:text-xl lg:text-2xl text-white/90 mb-12 sm:mb-16 
                         leading-relaxed max-w-3xl mx-auto"
              >
                Únete a miles de dueños que ya confían en DogScan AI para mantener
                a sus mascotas saludables y felices
              </motion.p>

              {/* Feature Cards */}
              <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-16">
                {highlights.map((highlight, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    onHoverStart={() => setHoveredIndex(index)}
                    onHoverEnd={() => setHoveredIndex(null)}
                    className="group relative"
                  >
                    <AnimatePresence mode="sync">
                      {hoveredIndex === index && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          className="absolute inset-0 bg-white/10 rounded-2xl blur-xl"
                        />
                      )}
                    </AnimatePresence>
                    <div className="relative flex flex-col items-center gap-4 bg-white/10 
                                 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 
                                 transition-all duration-300 group-hover:border-white/40 
                                 group-hover:transform group-hover:-translate-y-1"
                    >
                      <div className={`p-3 sm:p-4 rounded-xl bg-gradient-to-br ${highlight.color} 
                                   group-hover:bg-gradient-to-br group-hover:${highlight.hoverColor} 
                                   shadow-lg transition-all duration-300`}>
                        <highlight.icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                      </div>
                      <div className="text-center">
                        <h3 className="text-white font-semibold text-lg sm:text-xl mb-2">
                          {highlight.text}
                        </h3>
                        <p className="text-white/80 text-sm sm:text-base">
                          {highlight.subtext}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
              >
                <motion.div 
                  whileHover={{ scale: 1.02 }} 
                  whileTap={{ scale: 0.98 }}
                  className="w-full sm:w-auto"
                >
                  <Button
                    className="w-full sm:w-auto bg-white hover:bg-gray-50 text-orange-600 
                             px-8 sm:px-10 py-6 sm:py-7 shadow-lg hover:shadow-xl 
                             transition-all duration-300 text-lg sm:text-xl font-bold 
                             rounded-2xl group"
                    onClick={() => window.location.href = "/?sign-in=true"}
                  >
                    <span>Comenzar Ahora</span>
                    <ArrowUpRight className="ml-2 h-5 w-5 sm:h-6 sm:w-6 
                                         group-hover:translate-x-1 group-hover:-translate-y-1 
                                         transition-transform duration-300" />
                  </Button>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.02 }} 
                  whileTap={{ scale: 0.98 }}
                  className="w-full sm:w-auto"
                >
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white 
                             border-2 border-white/80 px-8 sm:px-10 py-6 sm:py-7 
                             shadow-lg hover:shadow-xl transition-all duration-300 
                             backdrop-blur-sm text-lg sm:text-xl font-bold rounded-2xl group"
                    onClick={() => document.getElementById('demo-section').scrollIntoView({ behavior: 'smooth' })}
                  >
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="mr-2"
                    >
                      <Play className="h-5 w-5 sm:h-6 sm:w-6 
                                   group-hover:fill-white transition-all duration-300" />
                    </motion.div>
                    Ver Demo
                  </Button>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTA;